/**
 * Web Reset Password Page
 *
 * Clean composition layer using the Template + Wrapper pattern.
 *
 * Architecture:
 * - Uses AuthLayoutTemplate for the two-column layout
 * - Uses ResetPasswordTemplate for the form
 * - Handles authentication logic via authService
 * - Extracts reset token from URL query parameters
 * - Follows the same pattern as Login.tsx and Register.tsx
 *
 * Benefits:
 * - Minimal code (wrapper delegates to templates)
 * - Easy to test and maintain
 * - Reusable templates
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayoutTemplate, ResetPasswordTemplate, showToast } from '@ideoz/ui';
import { authService } from '@ideoz/core';

export default function ResetPassword(): JSX.Element {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [token, setToken] = useState<string | undefined>();

    // Extract token from URL on mount
    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');

        if (!tokenFromUrl) {
            showToast({
                message: 'Invalid or missing reset token. Please request a new password reset link.',
                type: 'error',
            });
        } else {
            setToken(tokenFromUrl);
        }
    }, [searchParams]);

    // Handle reset password submission
    const handleSubmit = useCallback(
        async (password: string) => {
            if (!token) {
                showToast({
                    message: 'Invalid or missing reset token. Please request a new password reset link.',
                    type: 'error',
                });
                throw new Error('Invalid or missing reset token');
            }

            setIsLoading(true);

            try {
                await authService.resetPassword(token, password);

                // Show success toast
                showToast({
                    message: 'Password reset successful! You can now login with your new password.',
                    type: 'success',
                });

                // Redirect to login page after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } catch (error: any) {
                // Show error toast
                showToast({
                    message: error.message || 'Failed to reset password. Please try again.',
                    type: 'error',
                });
            } finally {
                setIsLoading(false);
            }
        },
        [token, navigate]
    );

    // Navigate to login page
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    return (
        <AuthLayoutTemplate isWeb={true}>
            <ResetPasswordTemplate
                onSubmit={handleSubmit}
                onNavigateToLogin={handleNavigateToLogin}
                isLoading={isLoading}
                isWeb={true}
                token={token}
            />
        </AuthLayoutTemplate>
    );
}
